import { Box, Typography, Stack, Grid, List as MuiList } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { Container } from 'shared-components/Container';
import { List } from 'components/common/List';
import { Text } from './styles';
import DefaultWrapper from 'shared-components/DefaultWrapper';
import { Title } from 'components/MyCareNetwork';
import { ReactComponent as PrinterIcon } from 'images/Consent/printer.svg';
import { PrintButtonWrapper } from '../Consent/styles';
import {
  useGetProfileParticipantTaskQuery,
  useGetProfileParticipantTasksQuery,
} from 'store/api/pkd/endpoints/participantTask';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { TaskTitles } from 'utils/enum';
import { BasicSkeleton } from 'components/SkeletonLoaders';
import { getUsersOpportunities } from './utils';

const CTEReport = () => {
  const theme = useTheme();
  const crumbs = [
    { title: 'Clinical Trial Eligibility Report', isActive: true },
  ];
  const {
    isPersonLoading,
    personAccountProfileId,
  } = useGetPersonAndAttributes();

  const { participantTask, isParticipantTaskLoading } =
    useGetProfileParticipantTasksQuery(
      { personAccountProfileId },
      {
        skip             : !personAccountProfileId,
        selectFromResult : ({ data = [], isFetching }) => ({
          participantTask : data?.find(
            (item) => item.taskTitle === TaskTitles.coreQuestionnaire
          ),
          isParticipantTaskLoading : isFetching,
        }),
      }
    );

  const { data: coreQuestionnaire, isLoading: isCoreQuestionnaireLoading } =
    useGetProfileParticipantTaskQuery(
      { personAccountProfileId, participantTaskId: participantTask?.id },
      { skip: !participantTask }
    );

  const isLoading =
    isPersonLoading || isCoreQuestionnaireLoading || isParticipantTaskLoading;

  return (
    <DefaultWrapper
      crumbs={crumbs}
      showHeader={false}
      margin={{ xs: '10px 15px 0px 15px', lg: '10px 78px 0px 78px' }}>
      <Box
        p='15px'>
        <PrintButtonWrapper
          text='Print'
          startIcon={<PrinterIcon />}
          color={theme.palette.primary.gray6}
          borderColor={theme.palette.primary.gray3}
          width='98px'
          onClickHandler={() => window.print()}
        />
        <Title
          mt='2rem'>Clinical Trial Eligibility Report</Title>
        <Text
          mb={4}>
          To develop drugs and therapies for PKD, clinical studies must be
          conducted. Patients play a key role in the research and development
          process by volunteering to participate in clinical studies. From
          observational studies to clinical trials, you can help researchers
          unlock the secrets of PKD and find a treatment by participating in a
          study.
          <br />
          <br />
          Based on what you’ve told the Registry about your disease journey, we
          believe you may qualify for the below. Please talk with the study team
          or your doctor about what participating might look like.
        </Text>
        {isLoading
          ? (
            <Box
              display='flex' alignItems='center'>
              <BasicSkeleton
                height='300px' width='50%'
              />
              <BasicSkeleton
                height='300px' width='50%'
              />
            </Box>
          )
          : (
            <>
              {getUsersOpportunities(coreQuestionnaire).length > 0
                ? (
                  <Grid
                    container spacing={5}>
                    {getUsersOpportunities(coreQuestionnaire).map((opportunity) => (
                      <Grid
                        item xs={12} md={6} key={opportunity.title}>
                        <Stack
                          maxHeight='100%'
                          gap='52px'
                          flexDirection={{ lg: 'row' }}>
                          <Container
                            sx={{ wordBreak: 'break-word' }}>
                            <strong>{opportunity.title}</strong>
                            <Text>{opportunity.subTitle}</Text>
                            <strong>{opportunity.eligibilityCriteria.title}</strong>
                            <List>
                              {opportunity.eligibilityCriteria.criteria.map(
                                (item) => (
                                  <>
                                    {typeof item === 'string'
                                      ? (
                                        <List.Item
                                          key={item}>{item}</List.Item>
                                      )
                                      : (
                                        <>
                                          <List.Item>
                                            {item?.[0]}
                                            <List>
                                              {item
                                                .filter(
                                                  (_, index) => index > 0
                                                )
                                                .map((moreItem) => (
                                                  <List.Item
                                                    key={moreItem}>
                                                    {moreItem}
                                                  </List.Item>
                                                ))}
                                            </List>
                                          </List.Item>
                                        </>
                                      )}
                                  </>
                                )
                              )}
                            </List>
                            <strong>{opportunity.studyLocation.title}</strong>
                            {opportunity.studyLocation.locations.length > 1
                              ? (
                                <List>
                                  {opportunity.studyLocation.locations.map(
                                    (item, index) => (
                                      <List.Item
                                        key={`${index}_${item}`}>
                                        {item}
                                      </List.Item>
                                    )
                                  )}
                                </List>
                              )
                              : (
                                <Text>{opportunity.studyLocation.locations[0]}</Text>
                              )}
                            {opportunity.note && <Text>{opportunity.note}</Text>}
                            <strong>{opportunity.learnMore.title}</strong>
                            {opportunity.learnMore.links.length > 1
                              ? (
                                <List>
                                  {opportunity.learnMore.links.map((item) => (
                                    <List.Item
                                      key={item}>{item}</List.Item>
                                  ))}
                                </List>
                              )
                              : (
                                <Box>
                                  <a
                                    href={opportunity.learnMore.links[0]}>
                                    {opportunity.learnMore.links[0]}
                                  </a>
                                </Box>
                              )}
                          </Container>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                )
                : (
                  <Typography
                    variant='h6'
                    fontFamily='Montserrat'
                    my={5}
                    fontWeight={'bolder'}>
                You are not eligible for any study opportunity at this time*
                  </Typography>
                )}
            </>
          )}
        <Box
          mt={3}>
          <Typography
            variant='pb5'
            fontFamily='Montserrat'
            component='p'
            mb={2}
            color={theme.palette.primary.grey5}>
            Choosing to participate in a study is an important personal
            decision. Talk with your doctor and family members or friends about
            deciding to join a study. The ADPKD Registry team has done our best
            to match you with studies for which we believe you may qualify based
            on available data; however, eligibility for a study is ultimately
            determined by the sponsor and study team.
          </Typography>
          <Typography
            variant='pb5'
            fontFamily='Montserrat'
            component='p'
            color={theme.palette.primary.grey5}>
            *Study details (including enrollment status and location) is updated
            quarterly
          </Typography>
        </Box>
      </Box>
    </DefaultWrapper>
  );
};

export default CTEReport;
